import React, { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Check, ArrowLeft, ArrowRight, Star } from "lucide-react";
import { WA, trackEvent, resolveMediaUrl } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { useLanguage } from "@/i18n/LanguageContext";
import { Reveal, Overline } from "@/components/site/SectionHeader";
import SEO from "@/components/site/SEO";
import { bi, biList } from "@/lib/bilingual";
import { useCachedGet } from "@/hooks/useCachedGet";
import PageSkeleton from "@/components/site/PageSkeleton";

const ProductDetail = () => {
  const { slug } = useParams();
  const { settings } = useSettings();
  const { t, lang } = useLanguage();
  const { data: product, ready } = useCachedGet(`/products/${slug}`);
  const { data: productsRaw } = useCachedGet("/products", { published: true });
  const related = Array.isArray(productsRaw)
    ? productsRaw.filter((p) => p.slug !== slug).slice(0, 3)
    : [];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!ready) return <PageSkeleton />;
  if (product === false) return <div className="pt-40 text-center text-zinc-500">{t("products.notFound")}</div>;
  if (!product) return <PageSkeleton />;

  const name = bi(product.name, lang);
  const desc = bi(product.description, lang);
  const category = bi(product.category, lang);
  const benefits = biList(product.benefits, lang);
  const specs = biList(product.specs, lang);
  const waMsg = t("products.waInterest", { name });
  const wa = WA(settings?.whatsapp_number, waMsg);

  return (
    <>
      <SEO
        title={name}
        description={desc}
        image={product.image}
        type="product"
        product={{ ...product, name, description: desc, category }}
        breadcrumbs={[
          { name: t("nav.home"), url: window.location.origin + "/" },
          { name: t("nav.products"), url: window.location.origin + "/produk" },
          { name, url: window.location.href },
        ]}
      />
      <section className="pt-10 pb-8 bg-[#fff5f5]">
        <div className="container-wide">
          <Link to="/produk" data-testid="back-to-products" className="inline-flex items-center gap-2 text-sm text-zinc-500 hover:text-maroon-800">
            <ArrowLeft size={14} /> {t("products.back")}
          </Link>
        </div>
      </section>

      <section className="pb-16 md:pb-24 bg-[#fff5f5]">
        <div className="container-wide grid lg:grid-cols-2 gap-12 items-start">
          <Reveal>
            <div className="aspect-square rounded-3xl overflow-hidden bg-white">
              <img src={resolveMediaUrl(product.image)} alt={name} className="w-full h-full object-contain p-6" loading="lazy" />
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <Overline>{category}</Overline>
            <h1 className="mt-4 text-4xl md:text-5xl font-display font-black text-zinc-900 tracking-tighter text-balance">{name}</h1>
            <p className="mt-5 text-zinc-600 leading-relaxed text-lg">{desc}</p>

            {benefits.length > 0 && (
              <div className="mt-8">
                <h3 className="text-sm font-bold uppercase tracking-widest text-maroon-800">{t("products.benefits")}</h3>
                <ul className="mt-4 grid sm:grid-cols-2 gap-3">
                  {benefits.map((b, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <span className="w-6 h-6 rounded-full bg-maroon-100 text-maroon-800 flex items-center justify-center shrink-0"><Check size={14} /></span>
                      <span className="text-zinc-700 text-sm">{b}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {specs.length > 0 && (
              <div className="mt-8">
                <h3 className="text-sm font-bold uppercase tracking-widest text-maroon-800">{t("products.specs")}</h3>
                <ul className="mt-4 space-y-2">
                  {specs.map((sp, i) => (
                    <li key={i} className="flex items-start gap-2 text-zinc-700 text-sm">
                      <Star size={14} className="text-amber-500 mt-1 shrink-0" /> {sp}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <a href={wa} target="_blank" rel="noopener noreferrer" data-testid="product-wa-cta"
              onClick={() => trackEvent("click", `/produk/${slug}`, "whatsapp")}
              className="mt-10 inline-flex items-center gap-2 px-7 py-4 rounded-full bg-maroon-800 text-white font-semibold hover:bg-maroon-900 hover:-translate-y-0.5 transition-all shadow-lg shadow-maroon-800/20">
              {t("common.consultWa")} <ArrowRight size={18} />
            </a>
          </Reveal>
        </div>
      </section>

      {related.length > 0 && (
        <section className="section-pad bg-white">
          <div className="container-wide">
            <h2 className="text-3xl md:text-4xl font-display font-bold text-zinc-900 tracking-tight">{t("products.related")}</h2>
            <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {related.map((p) => (
                <Link key={p.id} to={`/produk/${p.slug}`} className="group block">
                  <div className="aspect-[4/3] rounded-2xl overflow-hidden bg-white">
                    <img src={resolveMediaUrl(p.image)} alt={bi(p.name, lang)} className="w-full h-full object-contain p-4" loading="lazy" />
                  </div>
                  <div className="pt-4">
                    <div className="text-xs uppercase tracking-widest text-maroon-700 font-bold">{bi(p.category, lang)}</div>
                    <h3 className="mt-2 text-lg font-display font-bold text-zinc-900 group-hover:text-maroon-800">{bi(p.name, lang)}</h3>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </>
  );
};

export default ProductDetail;
